import React, { useEffect, useState } from "react";
import { Plus, Loader2, Edit2, Trash2, Check, X } from "lucide-react";
import { PageBody, PageHeader } from "@/components/AppLayout";
import { createProgram, listPrograms, updateProgram, deleteProgram } from "@/lib/api";
import BudgetProgress from "@/components/BudgetProgress";
import { useToast } from "@/hooks/use-toast";
import { PROGRAM } from "@/constants/testIds";
import { useAuth } from "@/lib/auth";

export default function Programs() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [programs, setPrograms] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [budget, setBudget] = useState("");
  const [desc, setDesc] = useState("");
  const [working, setWorking] = useState(false);

  // Edit program states
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editBudget, setEditBudget] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const userRoles = Array.isArray(user?.roles) && user.roles.length > 0
    ? user.roles
    : (user?.role ? [user.role] : []);

  const canManage = userRoles.some((r) =>
    ["admin", "organization_admin", "owner", "account_admin", "executive_director", "super_admin", "treasurer"].includes(
      String(r).toLowerCase()
    )
  );

  const load = () => listPrograms().then(setPrograms).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setWorking(true);
    try {
      await createProgram({ name, budget_amount: parseFloat(budget), description: desc || null });
      setName(""); setBudget(""); setDesc(""); setShowForm(false);
      toast({ title: "Program created" });
      await load();
    } catch (err) {
      toast({ title: "Could not create", description: err?.response?.data?.detail || err?.message, variant: "destructive" });
    } finally {
      setWorking(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditName(p.name || "");
    setEditBudget(p.budget_amount || "");
    setEditDesc(p.description || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditBudget("");
    setEditDesc("");
  };

  const handleSaveEdit = async (pId) => {
    if (!editName.trim()) {
      toast({ title: "Name required", description: "Program name cannot be empty.", variant: "destructive" });
      return;
    }
    setSavingEdit(true);
    try {
      await updateProgram(pId, {
        name: editName,
        budget_amount: parseFloat(editBudget || "0"),
        description: editDesc || null,
      });
      toast({ title: "Program updated" });
      cancelEdit();
      await load();
    } catch (err) {
      toast({ title: "Could not update program", description: err?.response?.data?.detail || err?.message, variant: "destructive" });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (pId) => {
    if (!window.confirm("Are you sure you want to delete this program?")) return;
    setDeletingId(pId);
    try {
      await deleteProgram(pId);
      setPrograms((prev) => prev.filter((p) => (p.id || p._id) !== pId));
      toast({ title: "Program deleted" });
      await load();
    } catch (err) {
      toast({ title: "Could not delete program", description: err?.response?.data?.detail || err?.response?.data?.message || err?.message, variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <PageHeader title="Programs & Budgets" subtitle="Allocate intent. Track impact.">
        {canManage && (
          <button
            data-testid={PROGRAM.newButton}
            onClick={() => setShowForm((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Plus size={14} /> {showForm ? "Cancel" : "New program"}
          </button>
        )}
      </PageHeader>
      <PageBody>
        {showForm && (
          <form onSubmit={submit} className="border border-border bg-card rounded-md p-6 mb-6 grid sm:grid-cols-12 gap-4">
            <label className="sm:col-span-4">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Name</span>
              <input data-testid={PROGRAM.nameInput} required value={name} onChange={(e) => setName(e.target.value)} className="td-input mt-1.5" placeholder="Food Pantry · April" />
            </label>
            <label className="sm:col-span-3">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Budget</span>
              <input data-testid={PROGRAM.budgetInput} required type="number" step="0.01" min="0" value={budget} onChange={(e) => setBudget(e.target.value)} className="td-input mono mt-1.5" placeholder="0.00" />
            </label>
            <label className="sm:col-span-5">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Description</span>
              <input data-testid={PROGRAM.descInput} value={desc} onChange={(e) => setDesc(e.target.value)} className="td-input mt-1.5" placeholder="What is this program for?" />
            </label>
            <div className="sm:col-span-12 flex justify-end">
              <button data-testid={PROGRAM.submit} disabled={working} className="rounded-md bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:bg-primary/90 disabled:opacity-60 inline-flex items-center gap-1.5">
                {working ? <Loader2 size={14} className="animate-spin" /> : null} Create program
              </button>
            </div>
          </form>
        )}

        {programs.length === 0 ? (
          <div className="border border-dashed border-border rounded-md p-12 text-center">
            <div className="serif text-xl text-primary">No programs yet</div>
            <p className="text-sm text-muted-foreground mt-1">Create a program to start allocating budgets.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {programs.map((p) => {
              const isEditing = editingId === p.id;
              return (
                <div key={p.id} data-testid={PROGRAM.row(p.id)} className="border border-border bg-card rounded-md p-6 relative group">
                  {isEditing ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Program Name</label>
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="td-input mt-1 text-xs py-1.5"
                            placeholder="Name"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Budget ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={editBudget}
                            onChange={(e) => setEditBudget(e.target.value)}
                            className="td-input mt-1 text-xs py-1.5 mono"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Description</label>
                        <input
                          type="text"
                          value={editDesc}
                          onChange={(e) => setEditDesc(e.target.value)}
                          className="td-input mt-1 text-xs py-1.5"
                          placeholder="Description"
                        />
                      </div>
                      <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                        <button
                          type="button"
                          onClick={cancelEdit}
                          disabled={savingEdit}
                          className="px-3 py-1.5 text-xs rounded border border-border text-muted-foreground hover:bg-accent transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSaveEdit(p.id)}
                          disabled={savingEdit}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                        >
                          {savingEdit ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 pr-12">
                          <div className="serif text-xl text-primary truncate">{p.name}</div>
                          {p.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{p.description}</p>}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Budget</div>
                          <div className="mono text-lg text-primary tabular">${Number(p.budget_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                      </div>

                      {canManage && (
                        <div className="absolute top-4 right-4 flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => startEdit(p)}
                            className="p-1.5 rounded text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                            title="Edit program"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(p.id)}
                            disabled={deletingId === p.id}
                            className="p-1.5 rounded text-destructive/70 hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                            title="Delete program"
                          >
                            {deletingId === p.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                          </button>
                        </div>
                      )}

                      <div className="mt-6">
                        <BudgetProgress testId={PROGRAM.progress(p.id)} value={Number(p.spent_amount)} max={Number(p.budget_amount)} />
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </PageBody>
      <style>{`
        .td-input { width: 100%; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); border-radius: 6px; padding: 10px 12px; font-size: 14px; color: hsl(var(--foreground)); }
        .td-input:focus { outline: none; border-color: hsl(var(--ring)); box-shadow: 0 0 0 3px hsl(var(--ring) / 0.12); }
      `}</style>
    </>
  );
}

